<template>
	<div class="config-helper-container">
		<div class="helper-box">
			<h1 class="title">OIDC 配置检查工具</h1>
			
			<div class="config-section">
				<h2>当前配置信息</h2>
				<div class="config-item">
					<label>认证服务器：</label>
					<span>{{ config.authority }}</span>
				</div>
				<div class="config-item">
					<label>客户端 ID：</label>
					<span>{{ config.client_id }}</span>
				</div>
				<div class="config-item">
					<label>登录回调 URL：</label>
					<span class="highlight">{{ config.redirect_uri }}</span>
					<el-button 
						type="text" 
						size="small" 
						@click="copyToClipboard(config.redirect_uri)"
						class="copy-btn"
					>
						<i class="el-icon-document-copy"></i> 复制
					</el-button>
				</div>
				<div class="config-item">
					<label>登出回调 URL：</label>
					<span class="highlight">{{ config.post_logout_redirect_uri }}</span>
					<el-button 
						type="text" 
						size="small" 
						@click="copyToClipboard(config.post_logout_redirect_uri)"
						class="copy-btn"
					>
						<i class="el-icon-document-copy"></i> 复制
					</el-button>
				</div>
			</div>

			<div class="error-section">
				<el-alert
					title="invalid_request 错误解决方案"
					type="warning"
					:closable="false"
					show-icon
				>
					<div class="solution-content">
						<p><strong>问题原因：</strong>回调 URL 未在认证服务器中配置</p>
						<p><strong>解决步骤：</strong></p>
						<ol>
							<li>登录认证服务器管理后台：<a :href="config.authority" target="_blank">{{ config.authority }}</a></li>
							<li>找到客户端配置（客户端ID: {{ config.client_id }}）</li>
							<li>添加以下回调 URL 到允许列表：</li>
						</ol>
						<div class="callback-url-box">
							<code>{{ config.redirect_uri }}</code>
							<el-button 
								type="primary" 
								size="small" 
								@click="copyToClipboard(config.redirect_uri)"
							>
								复制
							</el-button>
						</div>
						<p style="margin-top: 15px;">4. 确保客户端类型为 <strong>公共客户端（Public Client）</strong> 或 <strong>SPA</strong></p>
						<p>5. 保存配置后重新尝试登录</p>
					</div>
				</el-alert>
			</div>

			<div class="alternative-section">
				<h2>备用配置方案</h2>
				<p>如果当前配置不工作，可以尝试以下备用回调 URL 格式：</p>
				
				<div class="alternative-item" v-for="(alt, index) in alternativeUrls" :key="index">
					<div class="alt-label">方案 {{ index + 1 }}：</div>
					<div class="alt-url">
						<code>{{ alt }}</code>
						<el-button 
							type="text" 
							size="small" 
							@click="copyToClipboard(alt)"
						>
							复制
						</el-button>
					</div>
					<div class="alt-desc">{{ getAltDescription(index) }}</div>
				</div>
			</div>

			<div class="test-section">
				<h2>测试回调 URL</h2>
				<p>点击下面的按钮测试回调 URL 是否可访问：</p>
				<el-button 
					type="primary" 
					@click="testCallbackUrl"
					:loading="testing"
				>
					测试回调 URL
				</el-button>
				<div v-if="testResult" class="test-result" :class="testResult.success ? 'success' : 'error'">
					{{ testResult.message }}
				</div>
			</div>

			<div class="action-section">
				<el-button type="primary" @click="goToLogin">返回登录页面</el-button>
				<el-button @click="clearStorage">清除本地存储</el-button>
			</div>
		</div>
	</div>
</template>

<script>
import oidcService from '@/utils/oidcConfig'

export default {
	name: 'OidcConfigHelper',
	data() {
		return {
			config: {
				authority: 'https://auth.yiqifei.com',
				client_id: '745748667101253',
				redirect_uri: '',
				post_logout_redirect_uri: ''
			},
			testing: false,
			testResult: null,
			alternativeUrls: []
		}
	},
	created() {
		this.loadConfig()
		this.generateAlternativeUrls()
	},
	methods: {
		loadConfig() {
			// 从 oidcService 获取配置
			const settings = oidcService.userManager.settings
			this.config = {
				authority: settings.authority,
				client_id: settings.client_id,
				redirect_uri: settings.redirect_uri,
				post_logout_redirect_uri: settings.post_logout_redirect_uri
			}
		},
		
		generateAlternativeUrls() {
			const origin = window.location.origin
			const baseUrl = `${origin}${window.location.pathname}#`
			
			this.alternativeUrls = [
				`${origin}/signin-oidc.html`,
				`${baseUrl}/Acccount/signin-oidc`,
				`${origin}/signin-oidc`,
				`${origin}/#/Acccount/signin-oidc`,
				`${origin}/Acccount/signin-oidc`
			]
		},
		
		getAltDescription(index) {
			const descriptions = [
				'当前使用的配置（HTML 中间页面）',
				'Hash 路由格式（如果服务器支持）',
				'简单路径格式（无 hash）',
				'完整 hash 路由格式',
				'相对路径格式'
			]
			return descriptions[index] || ''
		},
		
		copyToClipboard(text) {
			const textarea = document.createElement('textarea')
			textarea.value = text
			document.body.appendChild(textarea)
			textarea.select()
			document.execCommand('copy')
			document.body.removeChild(textarea)
			this.$message.success('已复制到剪贴板')
		},
		
		async testCallbackUrl() {
			this.testing = true
			this.testResult = null
			
			try {
				// 尝试访问回调 URL
				const response = await fetch(this.config.redirect_uri, {
					method: 'HEAD',
					mode: 'no-cors'
				})
				
				this.testResult = {
					success: true,
					message: '回调 URL 可以访问（注意：这只是检查 URL 是否存在，不代表认证服务器已配置）'
				}
			} catch (error) {
				this.testResult = {
					success: false,
					message: '无法访问回调 URL: ' + error.message
				}
			} finally {
				this.testing = false
			}
		},
		
		goToLogin() {
			this.$router.push('/Acccount/oidcLogin')
		},
		
		clearStorage() {
			if (confirm('确定要清除所有 OIDC 相关的本地存储吗？')) {
				const keys = Object.keys(localStorage)
				keys.forEach(key => {
					if (key.includes('oidc') || key.includes('state')) {
						localStorage.removeItem(key)
					}
				})
				this.$message.success('已清除本地存储')
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.config-helper-container {
	min-height: 100vh;
	padding: 20px;
	background: #f5f7fa;
}

.helper-box {
	background: #fff;
	border-radius: 8px;
	padding: 30px;
	max-width: 900px;
	margin: 0 auto;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.title {
	font-size: 24px;
	font-weight: 600;
	color: #333;
	margin: 0 0 30px 0;
	text-align: center;
}

.config-section,
.error-section,
.alternative-section,
.test-section,
.action-section {
	margin-bottom: 30px;
}

.config-section h2,
.alternative-section h2,
.test-section h2 {
	font-size: 18px;
	font-weight: 600;
	color: #333;
	margin: 0 0 15px 0;
}

.config-item {
	display: flex;
	align-items: center;
	padding: 10px 0;
	border-bottom: 1px solid #eee;
	
	label {
		font-weight: 500;
		color: #666;
		min-width: 150px;
	}
	
	span {
		flex: 1;
		color: #333;
		
		&.highlight {
			color: #409EFF;
			font-weight: 500;
			font-family: monospace;
		}
	}
	
	.copy-btn {
		margin-left: 10px;
	}
}

.solution-content {
	text-align: left;
	
	ol {
		margin: 10px 0;
		padding-left: 20px;
		
		li {
			margin: 8px 0;
			line-height: 1.6;
		}
	}
}

.callback-url-box {
	background: #f5f7fa;
	border: 1px solid #e4e7ed;
	border-radius: 4px;
	padding: 15px;
	margin: 15px 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	
	code {
		flex: 1;
		font-size: 14px;
		color: #333;
		word-break: break-all;
	}
}

.alternative-item {
	margin-bottom: 20px;
	padding: 15px;
	background: #f9f9f9;
	border-radius: 4px;
	border-left: 3px solid #409EFF;
}

.alt-label {
	font-weight: 500;
	color: #666;
	margin-bottom: 8px;
}

.alt-url {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 5px;
	
	code {
		flex: 1;
		font-size: 13px;
		color: #333;
		word-break: break-all;
	}
}

.alt-desc {
	font-size: 12px;
	color: #999;
	margin-top: 5px;
}

.test-result {
	padding: 10px;
	border-radius: 4px;
	margin-top: 10px;
	
	&.success {
		background: #f0f9ff;
		color: #67c23a;
		border: 1px solid #b3e19d;
	}
	
	&.error {
		background: #fef0f0;
		color: #f56c6c;
		border: 1px solid #fbc4c4;
	}
}

.action-section {
	text-align: center;
	padding-top: 20px;
	border-top: 1px solid #eee;
	
	.el-button {
		margin: 0 10px;
	}
}
</style>













